import _ from 'lodash';
import React from 'react';

import {
  Button,
  Skeleton,
  Stack,
  Typography
} from '@mui/material';
import {
  errorUtil,
  fields,
  formUtil,
  validationUtil
} from 'iron-forge-react-modules';
import {
  useFieldArray,
  useForm
} from 'react-hook-form';

import Big from 'big.js';
import { FormModal } from 'modules';
import * as dataService from './dataService';
import * as types from './types';



const ControlledCheckboxField = fields.CheckboxField.genControllerComponent<types.IFormState>();
const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();



export interface IProps {
  itemId: string;
  onClose: () => void;
}

export const FC: React.FC<IProps> = props => {
  const queryTuple = dataService.useQuery(props.itemId);
  const [
    saveListingManagementStrategiesByItem,
    saveListingManagementStrategiesByItemTuple
  ] = dataService.useSaveListingManagementStrategiesByItem(props.itemId);
  const [modalStep, setModalStep] = React.useState<'selectStores' | 'updatePrice' | 'confirmation'>('selectStores');

  const form = useForm<types.IFormState>({
    defaultValues: {
      stores: []
    }
  });
  const fieldArray = useFieldArray({
    control: form.control,
    name: 'stores'
  });
  formUtil.useSetFormFields(queryTuple, form.setValue, ['stores']);



  const handleClickPrevious = React.useCallback(
    () => {
      if (modalStep === 'selectStores') {
        props.onClose();
      } else {
        setModalStep(prev => {
          if (modalStep === 'confirmation') {
            return 'updatePrice';
          } else if (modalStep === 'updatePrice') {
            return 'selectStores';
          }

          return prev;
        });
      }
    },
    [
      modalStep,
      props
    ]
  );



  const processSubmit = React.useCallback(
    async (formState: types.IFormState) => {
      if (modalStep === 'selectStores' && !_.some(formState.stores, l => l.isActive)) {
        window.alert("Must select at least 1 store to be active.");
        return;
      }

      if (modalStep === 'confirmation') {
        await errorUtil.errorWrap(async () => {
          const previousState = _.filter(queryTuple.data.formState.stores, s => s.isActive);
          const storeId2WasActive = _.mapValues(_.keyBy(previousState, s => s.storeId), v => v.isActive);

          const deactivatedStores = formState.stores.filter(s => !s.isActive && storeId2WasActive[s.storeId]);
          await saveListingManagementStrategiesByItem(formState, deactivatedStores.map(s => s.storeId));
          props.onClose();
        }, {});

      } else {
        form.setValue('stores', formState.stores);

        setModalStep(value => {
          if (value === 'selectStores') {
            return 'updatePrice';
          } else if (value === 'updatePrice') {
            return 'confirmation';
          }

          return value;
        });
      }
    },
    [
      form,
      modalStep,
      props,
      queryTuple.data.formState.stores,
      saveListingManagementStrategiesByItem
    ]
  );



  const loading = queryTuple.loading || saveListingManagementStrategiesByItemTuple.loading;

  return (
    <FormModal.FC
      title="Update Listings"
      onClose={props.onClose}
      fnGenElCloseButton={buttonProps => (
        <Button
          {...buttonProps}
          color="secondary"
          onClick={handleClickPrevious}
          disabled={loading}>
          {modalStep === 'selectStores' ? "Cancel" : "Previous"}
        </Button>
      )}
      fnGenElAcceptButton={buttonProps => (
        <Button
          {...buttonProps}
          type="submit"
          disabled={loading}>
          {modalStep === 'confirmation' ? "Complete" : "Next"}
        </Button>
      )}
      onSubmit={form.handleSubmit(processSubmit)}>
      {queryTuple.loading && _.times(5).map(i => <Skeleton key={i} />)}

      {modalStep === 'selectStores' && (
        <Stack>
          {fieldArray.fields.map((field, index) => (
            <ControlledCheckboxField
              isDisabled={!field.canEnable}
              key={field.storeId}
              label={field.storeName}
              fieldKey={`stores.${index}.isActive`}
              formControl={form.control} />
          ))}
        </Stack>
      )}

      {modalStep === 'updatePrice' && fieldArray.fields.filter(field => field.isActive).map((field, index) => (
        <ControlledTextField
          key={field.id}
          label={field.storeName}
          fieldKey={`stores.${index}.priceDollars`}
          formControl={form.control}
          extraValidationRules={{ pattern: validationUtil.reDecimalNumber }} />
      ))}

      {modalStep === 'confirmation' && fieldArray.fields.filter(field => {
        const oldStrategy = queryTuple.data.formState.stores.find(l => l.storeId === field.storeId);
        return oldStrategy?.isActive !== field.isActive || oldStrategy.priceDollars !== field.priceDollars;
      }).map(listing => {
        const oldListing = queryTuple.data.formState.stores.find(l => l.storeId === listing.storeId);
        return (
          <React.Fragment key={listing.id}>
            {oldListing?.isActive !== listing.isActive && (
              <Typography>
                {listing.storeName}: <code>{oldListing?.isActive ? "Active" : "Inactive"}</code> set to <code>{listing.isActive ? "Active" : "Inactive"}</code>
              </Typography>
            )}

            {!(new Big(oldListing?.priceDollars ?? 0).eq(new Big(listing.priceDollars))) && (
              <Typography>
                {listing.storeName}: Price <code>{oldListing?.priceDollars}</code> set to <code>{listing.priceDollars}</code>
              </Typography>
            )}
          </React.Fragment>
        );
      })}
    </FormModal.FC>
  );
};
